/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
// ════════════════════════════════════════════════════════════════
// QnAOverlay.jsx
// Chat overlay component for Myra Q&A during GSA.
// Uses MyraChatService.sendMessage() for AI responses.
// Command execution bridge: response.action → currentCommandsAudio.
// ════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import styles from "./SelfAssessment.module.css";

// Import MyraChatService — adjust path to match production
import MyraChatService from "../voice-agent/MyraChatService";
import MyraInteraction from "../self-assesment/MyraInteractionController";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";

const COMMAND_DEBOUNCE_MS = 500;

const QnAOverlay = forwardRef(function QnAOverlay({ isOpen, onClose, screenName, screenPayload, initialExchanges, setQaOpen }, ref) {
  const [exchanges, setExchanges] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [turnNumber, setTurnNumber] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const idCounterRef = useRef(0);
  const lastCommandTimeRef = useRef(0);
  const hasInitializedRef = useRef(false);
  const shouldFocusOnOpenRef = useRef(false);

  // Pre-populate exchanges from initialExchanges prop on mount
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (initialExchanges && initialExchanges.length > 0) {
      const seeded = initialExchanges.map((ex) => {
        const id = ++idCounterRef.current;
        return { id, question: ex.question, answer: ex.answer, source: ex.source || "initial" };
      });
      setExchanges(seeded);
      setExpandedId(seeded[seeded.length - 1].id);
    }
    hasInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset conversationHistory and turnNumber when screenName changes
  useEffect(() => {
    setConversationHistory([]);
    setTurnNumber(0);
  }, [screenName]);
  const executeCommandIfMatch = useCallback((response) => {
    if (!response?.action) return;
    if (typeof window === "undefined") return;

    const handler = window.currentCommandsAudio?.[response.action];
    if (!handler) return;

    const now = Date.now();
    if (now - lastCommandTimeRef.current < COMMAND_DEBOUNCE_MS) return;
    lastCommandTimeRef.current = now;

    handler(response.value, response.speech, response.intent);
  }, []);
  const actionstoCheck = ["SELECT_RPE", "SELECT_SYMPTOM", "SIGN_UP", "SKIP_VIDEO", "REPLAY_VIDEO", "PAUSE_VIDEO", "RESUME_VIDEO", "EXPAND_VIDEO", "MOVE_ON"];

  const addExchange = useCallback(
    (question, answer, source, shouldAutoSend = false) => {
      const id = ++idCounterRef.current;

      if (answer === null && shouldAutoSend) {
        // Create exchange with null answer, then auto-send
        setExchanges((prev) => [...prev, { id, question, answer: null, source }]);
        setExpandedId(id);

        // Trigger the send programmatically
        setTimeout(async () => {
          const newHistory = [...conversationHistory, { role: "user", content: question }];

          try {
            if (MyraChatService) {
              const response = await MyraChatService.sendMessage(question, window.trSessionId, newHistory, turnNumber + 1, screenPayload, screenName);
              console.log("[RESPONSE IS : ] : ", response);
              if (response?.intent === "command" || response?.intent === "data_input" || actionstoCheck.includes(response?.action)) {
                setQaOpen(false);
              } else {
                if ((window?.notOpenQNA && window.notOpenQNA === true) || window?.commandDetected === true) {
                  setQaOpen(false);
                } else {
                  setQaOpen(true);
                }
              }

              // const answerText = response?.response || response?.speech || "I'm not sure how to answer that right now.";
              const answerText = response?.response || response?.speech || "";
              if (answerText) {
                setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, answer: answerText } : ex)));
                setConversationHistory([...newHistory, { role: "assistant", content: answerText }]);
                setTurnNumber((prev) => prev + 1);
              }

              executeCommandIfMatch(response);
            }
          } catch (err) {
            const errorAnswer = "Sorry, something went wrong. Please try again.";
            setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, answer: errorAnswer } : ex)));
          }
        }, 100);

        return id;
      }

      // Original behavior for manual adds
      if (answer === null) {
        shouldFocusOnOpenRef.current = true;
      }
      setExchanges((prev) => [...prev, { id, question, answer, source }]);
      setExpandedId(id);
      return id;
    },
    [conversationHistory, turnNumber, screenName, screenPayload, executeCommandIfMatch],
  );

  useImperativeHandle(
    ref,
    () => ({
      addExchange,
      getExchanges: () => exchanges,
      focusExchange: (qaId) => {
        setExpandedId(qaId);
        requestAnimationFrame(() => {
          if (!bodyRef.current) return;
          const el = bodyRef.current.querySelector(`[data-qa-id="${qaId}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      },
    }),
    [addExchange, exchanges],
  );

  // Auto-scroll to bottom when exchanges change
  useEffect(() => {
    if (bodyRef.current) {
      requestAnimationFrame(() => {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      });
    }
  }, [exchanges, expandedId]);

  // Focus input when overlay opens for a new typed question only
  useEffect(() => {
    if (isOpen && inputRef.current && shouldFocusOnOpenRef.current) {
      shouldFocusOnOpenRef.current = false;
      requestAnimationFrame(() => inputRef.current.focus());
    }
  }, [isOpen]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;
    setInputValue("");

    const id = ++idCounterRef.current;
    const newExchange = { id, question: text, answer: null, source: "typed_in_overlay" };

    setExchanges((prev) => [...prev, newExchange]);
    setExpandedId(id);

    // Build conversation history entry
    const newHistory = [...conversationHistory, { role: "user", content: text }];

    try {
      // Production: use MyraChatService
      if (MyraChatService) {
        const response = await MyraChatService.sendMessage(text, window.trSessionId, newHistory, turnNumber + 1, screenPayload, screenName);
        console.log("[RESPONSE IS : ] : ", response);
        if (response?.intent === "command" || response?.intent === "data_input" || actionstoCheck.includes(response?.action)) {
          setQaOpen(false);
        } else {
          if ((window?.notOpenQNA && window.notOpenQNA === true) || window?.commandDetected === true) {
            setQaOpen(false);
          } else {
            setQaOpen(true);
          }
        }

        // const answerText = response?.response || response?.speech || "I'm not sure how to answer that right now. Try asking in a different way.";
        const answerText = response?.response || response?.speech || "";

        if (answerText) {
          setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, answer: answerText } : ex)));
          setConversationHistory([...newHistory, { role: "assistant", content: answerText }]);
        }
        setTurnNumber((prev) => prev + 1);

        executeCommandIfMatch(response);
      } else {
        // Fallback for development/prototype
        const fallbackAnswer = "That's a great question. In the full app, I'd have more context to give you a specific answer. For now, I'd encourage you to continue through the assessment — a lot of questions get answered along the way.";
        setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, answer: fallbackAnswer } : ex)));
        setConversationHistory([...newHistory, { role: "assistant", content: fallbackAnswer }]);
        setTurnNumber((prev) => prev + 1);
      }
    } catch (err) {
      const errorAnswer = "Sorry, something went wrong. Please try again.";
      setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, answer: errorAnswer } : ex)));
    }
  }, [inputValue, conversationHistory, turnNumber, screenName, screenPayload, executeCommandIfMatch]);

  const isDesktop = () => {
    return window.innerWidth > 768;
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (!isDesktop()) return;
      if (e.key === "Enter" && inputValue.trim()) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, inputValue],
  );

  const toggleExchange = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!isOpen) return null;

  const isPlanScreen = screenName === "plan";

  return (
    <div className={styles.qaOverlay} onClick={handleBackdropClick}>
      <div className={styles.qaCard} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.qaHeader}>
          <div className={styles.qaHeaderLeft}>
            <div className={styles.qaHeaderAvatar}>M</div>
            <div>
              <div className={styles.qaHeaderTitle}>Chat with Myra</div>
              <div className={styles.qaHeaderSub}>Ask anything about the assessment</div>
            </div>
          </div>
          <button className={styles.qaClose} onClick={onClose}>
            Back
          </button>
        </div>

        {/* Body */}
        <div className={styles.qaBody} ref={bodyRef}>
          {exchanges.map((ex) => {
            const isExpanded = expandedId === ex.id;
            return (
              <div key={ex.id} data-qa-id={ex.id} className={`${styles.qaExchange} ${isExpanded ? "" : styles.qaExchangeCollapsed}`} onClick={() => toggleExchange(ex.id)}>
                <div className={styles.qaExchangeHeader}>
                  <span className={styles.qaExchangeQIcon}>Q</span>
                  <span className={styles.qaExchangeQText}>{ex.question}</span>
                  <span className={styles.qaExchangeExpand}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
                {isExpanded && (
                  <div className={styles.qaExchangeBody}>
                    <div className={styles.qaExchangeUser}>{ex.question}</div>
                    <div className={styles.qaExchangeMyra}>
                      <div className={styles.qaExchangeMyraAvatar}>M</div>
                      {ex.answer ? (
                        <div className={styles.qaExchangeMyraBubble}>{ex.answer}</div>
                      ) : (
                        <div className={styles.qaExchangeMyraBubble}>
                          <div className={styles.qaTypingDots}>
                            <span />
                            <span />
                            <span />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Plan CTA (only on plan screen) */}
        {isPlanScreen && (
          <div className={styles.qaPlanCta}>
            <button
              onClick={() => {
                /* Sign-up flow */
              }}
            >
              Sign Up — It's Free
            </button>
          </div>
        )}

        {/* Input bar */}
        <div className={styles.qaInputBar}>
          <div className={styles.qaInputRow}>
            <input ref={inputRef} type="text" className={styles.qaInput} placeholder="Ask Myra a question…" autoComplete="off" value={inputValue} onChange={(e) => setInputValue(e.target.value)} {...(isDesktop() && { onKeyDown: handleKeyDown })} />
            <button className={styles.qaSend} disabled={!inputValue.trim()} onClick={handleSend}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export { QnAOverlay };
